import React, {useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import {useDispatch, useSelector} from "react-redux";
import Button from "../../base/button/button";
import classnames from "classnames";
import {ReactComponent as IconPlaceHolder} from "../../../../assets/images/whats-new-icon-placeholder.svg";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {createUUId, dateComparator, groupBy, hexToRGB} from "../../../../core/services/utils/utils";
import LazyImage from "../../app-specific/lazy-image";
import CacheService from "../../../../core/services/cache/cache_service";
import DateUtils from "../../../../core/services/date-utils";

const defaultApplication = {
    applicationToken: 'default-app-token',
    name: "Application-less",
    mainColorCode: '232, 103, 65',
};

/**
 * Given a list of applications and their version history, groups the data by their
 * @param data {any[]}
 */
const prepareVersions = (data) => {
    return [...(data ?? [])]
        .map(e => e.application
            ? {
                ...e,
                application: {
                    ...e.application,
                    mainColorCode: hexToRGB(e.application.mainColorCode)?.reduce((p, c, i, array) => p.concat(`${c}${i < (array.length - 1) ? `, ` : ''}`), '')
                }
            }
            : {
                ...e,
                application: defaultApplication,
            }
        )
        .filter(e => e.versions?.length)
        .map(e => ({
            application: e.application,
            versions: [...(e.versions ?? [])]
                ?.sort((a, b) => dateComparator(b.releaseDate, a.releaseDate))
                ?.map(version => ({
                    ...version,
                    version: Object
                        .entries(groupBy(version.version ?? [], (e) => e.type?.title))
                        .map(([typeTitle, versions]) => {
                            return [
                                typeTitle,
                                versions?.map(e => ({
                                    text: e.summary,
                                    id: createUUId(true),
                                }))
                            ]
                        })

                }))
        }))
}

const WhatsNewDialog = () => {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [activeColor, setActiveColor] = useState(null);
    const [loading, setLoading] = useState(false);
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.whatsNew] ?? {});
    const dispatch = useDispatch();
    const isMounted = useIsMounted();


    const data = useMemo(() => {
        if (!(state?.data ?? [])?.length)
            return [];
        return prepareVersions(state?.data ?? []);
    }, [state?.data])


    const selectedData = useMemo(() => {
        if (selectedIndex === -1 || selectedIndex >= data.length)
            return null
        return data[selectedIndex];
    }, [data, selectedIndex])


    /**
     * With each change in the open state of the dialog and the data given to the dialog:
     * - if the data does not exist or the state is not opened, resets the other state of this dialog.
     * - sets the selected index within the bounds of the data.
     */
    useEffect(() => {
        if (!state?.open || !data?.length) {
            setSelectedIndex(-1);
            setActiveColor(null);
            return;
        }
        if (selectedIndex === -1 && data.length) {
            setSelectedIndex(0)
            return;
        }
        if (selectedIndex >= data.length) {
            setSelectedIndex(data.length - 1);
        }
    }, [state?.open, data])


    /**
     * With each change in the selectedIndex:
     * - changes the active color of the app with the color of the application.
     */
    useEffect(() => {
        if (!data?.length || selectedIndex === -1)
            return;
        setActiveColor(data[selectedIndex].application.mainColorCode);
    }, [selectedIndex]);

    /**
     * Calls the api for indicating that it's needed to show  what is new dialog again
     * @return {Promise<void>}
     */
    const readNewVersions = async () => {
        setLoading(true);
        const response = await Api.readLatestApplicationsVersion();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            close();
        }
    }

    /**
     * Closes the current dialog and resets the state back to its initial value.
     */
    const close = () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.whatsNew, {open: false}));
        CacheService.whatsNewDialogToggled(true, true);
        if (state.onClose) {
            state.onClose();
        }
    }

    /**
     * Closes the dialog but also toggles the flag of the reading this dialog.
     */
    const remindUserLater = () => {
        close();
        CacheService.whatsNewDialogToggled(false, true);
    }

    return (
        <Dialog
            open={state?.open ?? false}
            setOpen={remindUserLater}
            maxWidth={'md'}
            disableEscapeKeyDown
            disableBackdropClick
            className={'whats-new-dialog'}
            style={{
                '--active-color': activeColor ?? defaultApplication.mainColorCode,
            }}
        >
            <Row className='no-gutters align-items-center w-100 transition'>
                <Row xs={12} className='no-gutters justify-content-center w-100 m-3'>
                    <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                        What is new
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className={'button-container'}>
                    {
                        data?.map(({application}, index) => (
                            <button
                                key={application.applicationToken}
                                onClick={() => setSelectedIndex(index)}
                                className={classnames(
                                    'application-button',
                                    {
                                        'active': index === selectedIndex,
                                    }
                                )}
                                style={{
                                    '--app-active-color': application.mainColorCode ?? defaultApplication.mainColorCode,
                                }}
                            >
                                <div className={'d-flex flex-row align-items-center justify-content-center'}>
                                    <LazyImage
                                        src={application.logoURL}
                                        noImage={<IconPlaceHolder className={'app-icon'}/>}
                                        alt={'icon'}
                                        className={'app-icon'}
                                    />
                                    <p>
                                        {application.name}
                                    </p>
                                </div>
                            </button>
                        ))
                    }
                </div>
                <div className={'ml-4 mt-1 application-body'}>
                    {
                        selectedData?.versions?.map((version) => (
                            <div key={version.id} className={'mb-5'}>
                                <div className={'version-title'}>
                                    <p className={'mr-2'}>
                                        {
                                            version.releaseDate &&
                                            DateUtils.serverToLocal(version.releaseDate).format(DateUtils.dateFormat)
                                        }
                                    </p>
                                    <p>
                                        ({version?.name ?? ''})
                                    </p>
                                </div>
                                {
                                    version.bizLearnLink &&
                                    <a
                                        href={version.bizLearnLink}
                                        rel={"noreferrer"}
                                        target={'_blank'}
                                        className={'feature-link'}
                                    >
                                        Learn in BizLearn
                                    </a>
                                }
                                {
                                    version.version.map(([name, list]) => (
                                        <div key={name} className={'mb-3 mt-3 ml-4'}>
                                            <p className={'feature-title'}>
                                                {name}
                                            </p>
                                            {list?.map(e => (
                                                <div key={e.id} className={'feature'}>
                                                    <div/>
                                                    <p>
                                                        {e.text}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ))
                                }
                            </div>
                        ))
                    }
                </div>
                <Divider className='divider'/>
                <div className={'d-flex flex-row align-items-center justify-content-between w-100'}>

                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'0 8px 0 8px'}
                        onClick={remindUserLater}
                    >
                        {
                            loading
                                ? "Loading..."
                                : "Remind me later"
                        }
                    </Button>
                    <Button
                        disabled={loading}
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                        onClick={readNewVersions}
                    >
                        {
                            loading
                                ? "Loading..."
                                : "Got it"
                        }
                    </Button>
                </div>
            </Row>
        </Dialog>
    );
}

export default WhatsNewDialog;
